import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ReusableTable, { TableColumn, TableAction, DropdownItem } from '../components/adminTable';
import { Modal, ConfirmModal, Button, FormGroup, FormLabel } from '../components/adminModals';
import Pagination from '../components/Pagination';
import { exportApprovalReportPdf } from '../utils/exportReportPdf';
import { useNotification } from '../../sharedComponents/Notifications';
import { useAuth } from '../../../auth/hooks/auth';
import {
    reportBatchService,
    ApprovalRequestItem,
    ApprovalStatus,
    ApprovalType,
    APPROVAL_STATUS_LABELS,
    APPROVAL_TYPE_LABELS,
} from '../services/adminAuth';
import { Eye, CheckCircle, XCircle, Download, Bell } from 'lucide-react';

// DESS (roleId 9) only sees the processed tabs on this page.
const DESS_ROLE_ID = 9;

// ── Status badge ────────────────────────────────────────────────────────────────

const approvalStatusStyle = (status: number): string => {
    switch (status) {
        case ApprovalStatus.Pending: return 'bg-yellow-50 text-yellow-700';
        case ApprovalStatus.Approved: return 'bg-green-50 text-green-700';
        case ApprovalStatus.Rejected: return 'bg-red-50 text-red-700';
        default: return 'bg-gray-100 text-gray-700';
    }
};

const ApprovalStatusBadge: React.FC<{ status: number }> = ({ status }) => (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${approvalStatusStyle(status)}`}>
        {APPROVAL_STATUS_LABELS[status] || 'Unknown'}
    </span>
);

const isPending = (status: number) => status === ApprovalStatus.Pending;

const TABS: { value: number; label: string; title: string; subTitle: string }[] = [
    { value: ApprovalStatus.Pending, label: 'Pending', title: 'Pending Requests', subTitle: 'Report batches awaiting your decision' },
    { value: ApprovalStatus.Approved, label: 'Approved', title: 'Approved Requests', subTitle: 'Report batches you have approved' },
    { value: ApprovalStatus.Rejected, label: 'Rejected', title: 'Rejected Requests', subTitle: 'Report batches you have rejected' },
];

function ApprovalPage() {
    const { showNotification } = useNotification();
    const navigate = useNavigate();
    const { user } = useAuth();

    // DESS only reviews history — they see the Approved / Rejected tabs, not Pending.
    const isDess = user?.roleId === DESS_ROLE_ID;
    const visibleTabs = isDess ? TABS.filter(t => t.value !== ApprovalStatus.Pending) : TABS;

    // ── Table state ──────────────────────────────────────────────────────────────
    const [requests, setRequests] = useState<ApprovalRequestItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [search, setSearch] = useState('');
    // The active tab drives the approvalStatus sent to the API (a required, non-nullable enum).
    const [statusFilter, setStatusFilter] = useState<number>(isDess ? ApprovalStatus.Approved : ApprovalStatus.Pending);

    // ── Process (approve/reject) modal state ─────────────────────────────────────
    const [processTarget, setProcessTarget] = useState<ApprovalRequestItem | null>(null);
    const [processApprove, setProcessApprove] = useState(true);
    const [comment, setComment] = useState('');
    const [processing, setProcessing] = useState(false);

    // ── Notify authors confirm state ─────────────────────────────────────────────
    const [notifyTarget, setNotifyTarget] = useState<ApprovalRequestItem | null>(null);
    const [notifying, setNotifying] = useState(false);

    // ── Fetch requests ───────────────────────────────────────────────────────────
    const fetchRequests = async () => {
        setLoading(true);
        try {
            const response = await reportBatchService.getApprovalRequest({
                pageNumber,
                pageSize,
                sortBy: 'RequestedAt',
                sortDescending: true,
                search,
                approvalStatus: statusFilter,
            });
            if (response.isSuccess) {
                setRequests(response.data.data);
                setTotalRecords(response.data.totalRecords);
                setTotalPages(response.data.totalPages);
            } else {
                showNotification('error', { title: 'Error', message: response.message || 'Failed to fetch approval requests' });
            }
        } catch (err) {
            showNotification('error', { title: 'Error', message: 'An error occurred while fetching approval requests' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const handler = setTimeout(() => { fetchRequests(); }, 400);
        return () => clearTimeout(handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageNumber, pageSize, search, statusFilter]);

    // View opens a dedicated full page with the batch records listed in a table.
    const goToView = (request: ApprovalRequestItem) =>
        navigate(`/admin/approvals/report/${request.reportBatchId}`, { state: { request } });

    // ── Process (approve / reject) ───────────────────────────────────────────────
    const openProcess = (request: ApprovalRequestItem, approve: boolean) => {
        setProcessTarget(request);
        setProcessApprove(approve);
        setComment('');
    };

    const handleProcess = async () => {
        if (!processTarget) return;
        if (!processApprove && !comment.trim()) {
            showNotification('warning', { title: 'Validation Error', message: 'Please provide a reason for rejection.' });
            return;
        }

        setProcessing(true);
        try {
            const response = await reportBatchService.processApprovalRequest({
                approvalRequestId: processTarget.id,
                isApproved: processApprove,
                comment: comment.trim(),
            });
            if (response.isSuccess) {
                showNotification('success', {
                    title: 'Success',
                    message: `Request ${processApprove ? 'approved' : 'rejected'} successfully`,
                    timer: 3000,
                });
                setProcessTarget(null);
                setComment('');
                await fetchRequests();
            } else {
                showNotification('error', { title: 'Error', message: response.message || 'Failed to process the request' });
            }
        } catch (err) {
            showNotification('error', { title: 'Error', message: 'An error occurred while processing the request' });
        } finally {
            setProcessing(false);
        }
    };

    // ── Notify approved authors ──────────────────────────────────────────────────
    const handleConfirmNotify = async () => {
        if (!notifyTarget) return;
        setNotifying(true);
        try {
            const response = await reportBatchService.sendNotificationToApprovedAuthors(notifyTarget.reportBatchId);
            if (response.isSuccess) {
                showNotification('success', { title: 'Success', message: 'Authors have been notified of the approval', timer: 3000 });
                setNotifyTarget(null);
                await fetchRequests();
            } else {
                showNotification('error', { title: 'Error', message: response.message || 'Failed to notify authors' });
            }
        } catch (err) {
            showNotification('error', { title: 'Error', message: 'An error occurred while notifying authors' });
        } finally {
            setNotifying(false);
        }
    };

    // ── Table columns ────────────────────────────────────────────────────────────
    const columns: TableColumn<ApprovalRequestItem>[] = [
        {
            key: 'sn',
            label: 'S/N',
            sortable: false,
            render: (_v, _row, index) => <span className="text-gray-700">{(pageNumber - 1) * pageSize + (index ?? 0) + 1}</span>,
        },
        {
            key: 'title',
            label: 'Title',
            className: 'font-medium',
            render: (value, row) => (
                <div>
                    <div className="font-medium text-gray-900">{value || 'Approval Request'}</div>
                    <div className="text-xs text-gray-500">{APPROVAL_TYPE_LABELS[row.reportType] || '—'}</div>
                    {row.periodLabel && <div className="text-xs text-gray-400">{row.periodLabel}</div>}
                </div>
            ),
        },
        {
            key: 'nameOfRequestedUser',
            label: 'Requested By',
            sortValue: (row) => row.nameOfRequestedUser,
            render: (value, row) => (
                <div>
                    <div className="font-medium text-gray-900">{value || '—'}</div>
                    <div className="text-xs text-gray-500">{row.requestedAt ? new Date(row.requestedAt).toLocaleDateString() : ''}</div>
                </div>
            ),
        },
        {
            key: 'totalRecords',
            label: 'Records',
            render: (value) => <span className="text-gray-700">{value}</span>,
        },
        {
            key: 'status',
            label: 'Status',
            render: (value) => <ApprovalStatusBadge status={value as number} />,
        },
        // Only applies once a decision has been made; label reflects the tab (Approved By / Rejected By).
        ...(statusFilter === ApprovalStatus.Pending ? [] : [{
            key: 'nameOfApprovingUser',
            label: statusFilter === ApprovalStatus.Approved ? 'Approved By' : 'Rejected By',
            sortable: false,
            render: (value: any, row: ApprovalRequestItem) => (
                value
                    ? <div>
                        <div className="text-gray-700">{value}</div>
                        {row.approvedAt && <div className="text-xs text-gray-500">{new Date(row.approvedAt).toLocaleDateString()}</div>}
                    </div>
                    : <span className="text-gray-400">—</span>
            ),
        }]),
        // On the Approved tab, show whether the authors have been notified.
        ...(statusFilter === ApprovalStatus.Approved ? [{
            key: 'hasNotifiedAuthor',
            label: 'Authors Notified',
            sortable: false,
            render: (value: any) => (
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${value ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                    {value ? 'Notified' : 'Not notified'}
                </span>
            ),
        }] : []),
    ];

    const getActions = (row: ApprovalRequestItem): TableAction<ApprovalRequestItem>[] => {
        const items: DropdownItem<ApprovalRequestItem>[] = [
            {
                icon: <Eye size={16} />,
                label: 'View Report',
                onClick: (r: ApprovalRequestItem) => goToView(r),
                className: 'text-blue-600',
            },
        ];
        if (isPending(row.status)) {
            items.push(
                {
                    icon: <CheckCircle size={16} />,
                    label: 'Approve',
                    onClick: (r: ApprovalRequestItem) => openProcess(r, true),
                    className: 'text-green-600',
                },
                {
                    icon: <XCircle size={16} />,
                    label: 'Reject',
                    onClick: (r: ApprovalRequestItem) => openProcess(r, false),
                    className: 'text-red-600',
                },
            );
        } else {
            // Processed (Approved / Rejected) rows can export the report as a PDF.
            items.push({
                icon: <Download size={16} />,
                label: 'Export Report',
                onClick: (r: ApprovalRequestItem) => exportApprovalReportPdf(r, showNotification),
                className: 'text-gray-700',
            });
        }
        // Only DESS can notify approved authors of Successful Submissions; disabled once notified.
        if (isDess && row.status === ApprovalStatus.Approved && row.reportType === ApprovalType.SuccessfulSubmissions) {
            items.push({
                icon: <Bell size={16} />,
                label: (r: ApprovalRequestItem) => r.hasNotifiedAuthor ? 'Authors Notified' : 'Notify Authors',
                onClick: (r: ApprovalRequestItem) => setNotifyTarget(r),
                className: 'text-green-700',
                disabled: (r: ApprovalRequestItem) => r.hasNotifiedAuthor,
            });
        }
        return [{ variant: 'dropdown', onClick: () => undefined, title: 'Actions', dropdownItems: items }];
    };

    const activeTab = TABS.find(t => t.value === statusFilter) ?? TABS[0];

    return (
        <div className="w-full">
            <div className="flex flex-wrap -mx-[15px]">
                <div className="w-full px-[15px]">
                    {/* Header */}
                    <div className="mb-6">
                        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Report Approvals</h1>
                        <p className="text-gray-500 text-sm sm:text-base">
                            Review reports submitted for approval and approve or reject them.
                        </p>
                    </div>

                    {/* Status tabs — segmented control style */}
                    <div className="bg-gray-100 p-1 rounded-xl w-full mb-6">
                        <div className="flex gap-1 w-full">
                            {visibleTabs.map(t => (
                                <button
                                    key={t.value}
                                    onClick={() => { setStatusFilter(t.value); setPageNumber(1); }}
                                    className={`flex-1 relative px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 flex items-center justify-center gap-2 ${
                                        statusFilter === t.value
                                            ? 'bg-white text-gray-900 shadow-sm ring-1 ring-black/5'
                                            : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                                    }`}
                                >
                                    {t.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <ReusableTable
                        title={activeTab.title}
                        subTitle={activeTab.subTitle}
                        columns={columns}
                        data={requests}
                        getActions={getActions}
                        loading={loading}
                        showSearch={true}
                        onSearch={(q) => { setSearch(q); setPageNumber(1); }}
                        searchPlaceholder="Search requests..."
                        emptyMessage="No approval requests found."
                    />

                    {!loading && totalRecords > 0 && (
                        <Pagination
                            currentPage={pageNumber}
                            totalPages={totalPages}
                            totalRecords={totalRecords}
                            pageSize={pageSize}
                            onPageChange={setPageNumber}
                            onPageSizeChange={(size) => { setPageSize(size); setPageNumber(1); }}
                        />
                    )}

                    {/* Process (approve / reject) Modal */}
                    <Modal
                        isOpen={processTarget !== null}
                        onClose={() => { setProcessTarget(null); setComment(''); }}
                        title={processApprove ? 'Approve Request' : 'Reject Request'}
                        description={`${processApprove ? 'Approve' : 'Reject'} "${processTarget?.title || 'this request'}"${processTarget?.nameOfRequestedUser ? ` requested by ${processTarget.nameOfRequestedUser}` : ''}.`}
                    >
                        <div>
                            <FormGroup>
                                <FormLabel required={!processApprove}>Comment{processApprove ? ' (optional)' : ''}</FormLabel>
                                <textarea
                                    value={comment}
                                    onChange={(e) => setComment(e.target.value)}
                                    rows={4}
                                    placeholder={processApprove ? 'Add an optional comment...' : 'Provide a reason for rejection...'}
                                    disabled={processing}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                                />
                            </FormGroup>

                            <div className="flex gap-3 mt-6">
                                <Button variant="white" onClick={() => { setProcessTarget(null); setComment(''); }} className="flex-1" disabled={processing}>
                                    Cancel
                                </Button>
                                <Button
                                    variant={processApprove ? 'success' : 'danger'}
                                    onClick={handleProcess}
                                    className="flex-1"
                                    disabled={processing}
                                >
                                    {processing ? 'Processing...' : processApprove ? 'Approve' : 'Reject'}
                                </Button>
                            </div>
                        </div>
                    </Modal>

                    {/* Notify Authors Confirmation */}
                    <ConfirmModal
                        isOpen={notifyTarget !== null}
                        onClose={() => setNotifyTarget(null)}
                        onConfirm={handleConfirmNotify}
                        title="Notify Authors of Approval"
                        message={`Send an approval notification to the authors in "${notifyTarget?.title || notifyTarget?.periodLabel || 'this request'}"? They will be informed that their report has been approved.`}
                        confirmText="Notify Authors"
                        cancelText="Cancel"
                        variant="info"
                        isSubmitting={notifying}
                    />
                </div>
            </div>
        </div>
    );
}

export default ApprovalPage;
